/**
 * VERIFY WALLET BUTTON COMPONENT
 * 
 * Handles the wallet verification flow:
 * 1. Generates a challenge message with nonce, timestamp, and expiry
 * 2. Requests wallet signature via signMessage
 * 3. Sends signature to Edge Function for verification
 * 4. Updates UI on success/failure
 */

import { useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { supabase } from '../lib/supabase';

// Helper to convert Uint8Array to base64
function uint8ArrayToBase64(bytes) {
  return btoa(String.fromCharCode(...bytes));
}

// Generate a UUID v4
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export default function VerifyWalletButton({ userId, onVerified, className = '' }) {
  const { publicKey, signMessage } = useWallet();
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState(null);

  const handleVerify = async () => {
    // Clear previous errors
    setError(null);

    // Validate wallet connection
    if (!publicKey) {
      setError('Please connect your wallet first');
      return;
    }

    if (!signMessage) {
      setError('Your wallet does not support message signing. Please use a compatible wallet (e.g., Phantom, Solflare).');
      return;
    }

    if (!userId) {
      setError('User ID is required for verification');
      return;
    }

    setIsVerifying(true);

    try {
      // Get wallet address (Base58, case-sensitive)
      const address = publicKey.toBase58();
      
      // Generate challenge parameters
      const nonce = generateUUID();
      const now = new Date();
      const expiry = new Date(now.getTime() + 5 * 60 * 1000); // 5 minutes from now

      // Get domain from environment or use default
      const domain = import.meta.env.VITE_VERIFY_DOMAIN || 'outsera.app';

      // Construct challenge message
      const message = `Sign in to Outsera

Domain: ${domain}
Address: ${address}
Nonce: ${nonce}
Issued: ${now.toISOString()}
Expires: ${expiry.toISOString()}

By signing, you prove ownership of this wallet.`;

      console.log('🔐 Requesting signature for challenge...');
      
      // Request signature from wallet
      let signature;
      try {
        signature = await signMessage(new TextEncoder().encode(message));
      } catch (signError) {
        console.error('Signature error:', signError);
        if (signError.message?.includes('User rejected')) {
          throw new Error('Signature request was cancelled');
        }
        throw new Error('Failed to sign message: ' + (signError.message || 'Unknown error'));
      }

      // Convert signature to base64
      const signatureBase64 = uint8ArrayToBase64(signature);

      console.log('✅ Signature obtained, verifying...');

      // Optional: Store challenge in database for audit trail
      try {
        await supabase.from('wallet_challenges').insert({
          address,
          nonce,
          expires_at: expiry.toISOString()
        });
      } catch (challengeError) {
        console.warn('Could not store challenge:', challengeError);
        // Non-critical, continue
      }

      // Call Edge Function to verify signature
      const { data: functionData, error: functionError } = await supabase.functions.invoke(
        'verify_wallet',
        {
          body: {
            user_id: userId,
            address,
            signature: signatureBase64,
            message,
            nonce
          }
        }
      );

      if (functionError) {
        console.error('Verification function error:', functionError);
        throw new Error(functionError.message || 'Verification failed');
      }

      if (!functionData?.ok) {
        throw new Error(functionData?.error || 'Verification failed');
      }

      console.log('🎉 Wallet verified successfully!');

      // Call success callback if provided
      if (onVerified) {
        onVerified({ address, userId, verifiedAt: functionData.verified_at });
      }

    } catch (err) {
      console.error('Verification error:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <button
        onClick={handleVerify}
        disabled={isVerifying || !publicKey}
        className={`px-4 py-2 rounded-lg font-medium transition-all ${
          isVerifying || !publicKey
            ? 'bg-gray-600 cursor-not-allowed opacity-50'
            : 'bg-indigo-600 hover:bg-indigo-700'
        } text-white ${className}`}
      >
        {isVerifying ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Verifying...
          </span>
        ) : (
          'Verify Wallet Ownership'
        )}
      </button>

      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-3 py-2 rounded-lg text-sm">
          {error}
        </div>
      )}
    </div>
  );
}

